import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Popconfirm, Card, Select, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined } from '@ant-design/icons';
import {
  fetchBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  type BankAccountDto,
} from '../services/cashService';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

interface Branch {
  id: number;
  branchName: string;
}

const BankAccounts: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [bankAccounts, setBankAccounts] = useState<BankAccountDto[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadBankAccounts();
    loadBranches();
  }, []);

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      const data = await fetchBankAccounts();
      setBankAccounts(data);
    } catch (error) {
      message.error('Banka hesapları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await api.get<Branch[]>('/branches');
      setBranches(data);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  };

  const handleAdd = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({ currency: 'TRY', isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (record: BankAccountDto) => {
    setEditingAccount(record);
    form.setFieldsValue({
      bankName: record.bankName,
      accountName: record.accountName,
      accountCode: record.accountCode,
      iban: record.iban,
      branchId: record.branchId,
      openingBalance: record.openingBalance,
      currency: record.currency,
      description: record.description,
      isActive: record.isActive,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteBankAccount(id);
      message.success('Banka hesabı başarıyla silindi');
      loadBankAccounts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Banka hesabı silinirken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingAccount) {
        await updateBankAccount(editingAccount.id, values);
        message.success('Banka hesabı başarıyla güncellendi');
      } else {
        await createBankAccount(values);
        message.success('Banka hesabı başarıyla eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      loadBankAccounts();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'Banka hesabı kaydedilirken hata oluştu');
    }
  };

  const columns = [
    {
      title: 'Banka Adı',
      dataIndex: 'bankName',
      key: 'bankName',
    },
    {
      title: 'Hesap Adı',
      dataIndex: 'accountName',
      key: 'accountName',
    },
    {
      title: 'Hesap Kodu',
      dataIndex: 'accountCode',
      key: 'accountCode',
    },
    {
      title: 'Şube',
      dataIndex: 'branchId',
      key: 'branchId',
      render: (branchId: number) => {
        const branch = branches.find((b) => b.id === branchId);
        return branch ? branch.branchName : '-';
      },
    },
    {
      title: 'Bakiye',
      dataIndex: 'currentBalance',
      key: 'currentBalance',
      render: (amount: number, record: BankAccountDto) => (
        <Tag color={amount >= 0 ? 'green' : 'red'}>
          {Number(amount).toFixed(2)} {record.currency}
        </Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Aktif' : 'Pasif'}</Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: BankAccountDto) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
            <Popconfirm
              title="Bu banka hesabını silmek istediğinizden emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button type="primary" danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Banka Yönetimi"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Yeni Banka Hesabı Ekle
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={bankAccounts}
          rowKey="id"
          loading={loading}
          scroll={{ x: 600 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
        />
      </Card>

      <Modal
        title={editingAccount ? 'Banka Hesabını Düzenle' : 'Yeni Banka Hesabı Ekle'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="bankName"
            label="Banka Adı"
            rules={[{ required: true, message: 'Lütfen banka adı girin' }]}
          >
            <Input placeholder="Örn: Ziraat Bankası, İş Bankası" />
          </Form.Item>

          <Form.Item
            name="accountName"
            label="Hesap Adı"
            rules={[{ required: true, message: 'Lütfen hesap adı girin' }]}
          >
            <Input placeholder="Örn: Ana Hesap" />
          </Form.Item>

          <Form.Item name="accountCode" label="Hesap Kodu">
            <Input placeholder="Opsiyonel" />
          </Form.Item>

          <Form.Item name="iban" label="IBAN">
            <Input placeholder="TR00 0000 0000 0000 0000 0000 00" />
          </Form.Item>

          <Form.Item name="branchId" label="Şube">
            <Select placeholder="Şube seçin (opsiyonel)" allowClear>
              {branches.map((branch) => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {!editingAccount && (
            <Form.Item
              name="openingBalance"
              label="Açılış Bakiyesi"
              rules={[{ required: true, message: 'Lütfen açılış bakiyesi girin' }]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="0.00" min={0} precision={2} />
            </Form.Item>
          )}

          <Form.Item
            name="currency"
            label="Para Birimi"
            rules={[{ required: true, message: 'Lütfen para birimi seçin' }]}
          >
            <Select>
              <Select.Option value="TRY">TRY</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} placeholder="Opsiyonel" />
          </Form.Item>

          <Form.Item name="isActive" label="Aktif" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BankAccounts;
